import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Mic,
  MicOff,
  Upload,
  Play,
  Square,
  FileAudio,
  Clock,
  FileText,
  Loader2,
  Phone,
  Video,
  Download,
  Trash2,
  Eye,
  Brain,
  CheckCircle2,
  AlertCircle,
  Waves,
  Radio,
  Tractor,
  Mail,
  Calendar,
  ListTodo,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReunionsPage() {
  const { token, user } = useAuth();
  
  // États enregistrement
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [titre, setTitre] = useState('');
  const [transcriptionLive, setTranscriptionLive] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);
  const [streamingStatus, setStreamingStatus] = useState('idle'); // idle, connecting, streaming, processing
  const [chunksTranscribed, setChunksTranscribed] = useState(0);
  
  // États pour l'import
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // États liste réunions
  const [reunions, setReunions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReunion, setSelectedReunion] = useState(null);
  
  // Refs pour l'enregistrement
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const wsRef = useRef(null);
  const audioWorkletRef = useRef(null);

  // Charger les réunions
  const loadReunions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/reunions/liste`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setReunions(data.reunions || []);
    } catch (error) {
      console.error('Erreur chargement réunions:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadReunions();
  }, [loadReunions]);

  // Timer d'enregistrement
  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRecording]);

  // Formater le temps
  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Démarrer l'enregistrement avec transcription en temps réel
  const startRecording = async () => {
    try {
      setStreamingStatus('connecting');
      
      // Demander l'accès au micro
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        } 
      });
      streamRef.current = stream;

      // Créer le contexte audio pour la visualisation
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 16000
      });
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      // MediaRecorder pour l'enregistrement par chunks
      // Vérifier la compatibilité du format
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/mp4';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = ''; // Laisser le navigateur choisir
          }
        }
      }
      console.log('MediaRecorder mimeType:', mimeType || 'default');
      
      const recorderOptions = mimeType ? { mimeType } : {};
      mediaRecorderRef.current = new MediaRecorder(stream, recorderOptions);
      
      chunksRef.current = [];
      let chunkIndex = 0;
      
      mediaRecorderRef.current.ondataavailable = async (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
          
          // Toutes les 30 secondes (3 chunks de 10s), envoyer pour transcription
          if (chunksRef.current.length >= 3) {
            const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
            chunksRef.current = []; // Reset pour le prochain lot
            
            // Envoyer ce chunk pour transcription
            transcribeChunk(audioBlob, chunkIndex);
            chunkIndex++;
          }
        }
      };

      // Enregistrer par morceaux de 10 secondes
      mediaRecorderRef.current.start(10000);
      
      setIsRecording(true);
      setRecordingTime(0);
      setTranscriptionLive('');
      setChunksTranscribed(0);
      setStreamingStatus('streaming');
      
      // Visualisation du niveau audio
      const updateAudioLevel = () => {
        if (analyserRef.current && streamRef.current) {
          const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
          analyserRef.current.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
          setAudioLevel(average / 255);
          if (isRecording) {
            requestAnimationFrame(updateAudioLevel);
          }
        }
      };
      updateAudioLevel();
      
      toast.success('Enregistrement démarré', {
        description: 'Transcription en temps réel active - parlez clairement'
      });

    } catch (error) {
      console.error('Erreur démarrage enregistrement:', error);
      setStreamingStatus('idle');
      toast.error('Erreur micro', {
        description: error.message || 'Impossible d\'accéder au microphone'
      });
    }
  };

  // Transcrire un chunk audio
  const transcribeChunk = async (audioBlob, chunkIndex) => {
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, `chunk_${chunkIndex}.webm`);

      const res = await fetch(`${API_URL}/api/transcription/chunk`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      const data = await res.json();
      
      if (data.success && data.transcription) {
        setTranscriptionLive(prev => prev + ' ' + data.transcription);
        setChunksTranscribed(prev => prev + 1);
      }
    } catch (error) {
      console.error('Erreur transcription chunk:', error);
    }
  };

  // Arrêter l'enregistrement
  const stopRecording = async () => {
    if (!mediaRecorderRef.current) return;

    setStreamingStatus('processing');

    return new Promise((resolve) => {
      mediaRecorderRef.current.onstop = async () => {
        // Transcrire les derniers chunks restants
        if (chunksRef.current.length > 0) {
          const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
          await transcribeChunk(audioBlob, chunksTranscribed);
        }
        
        // Arrêter le stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
        
        // Fermer le contexte audio
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }

        // Sauvegarder la réunion complète avec la transcription
        if (transcriptionLive.trim()) {
          await saveCompletedReunion();
        }

        setIsRecording(false);
        setAudioLevel(0);
        setStreamingStatus('idle');
        
        resolve();
      };

      mediaRecorderRef.current.stop();
    });
  };

  // Sauvegarder la réunion terminée
  const saveCompletedReunion = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reunions/creer`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          titre: titre || `Réunion du ${new Date().toLocaleDateString('fr-FR')}`,
          type_reunion: 'appel',
          notes: transcriptionLive
        })
      });

      const data = await res.json();
      
      if (data.success) {
        // Mettre à jour avec la transcription
        await fetch(`${API_URL}/api/reunions/${data.reunion_id}`, {
          method: 'PATCH',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            transcription: {
              status: 'completed',
              text: transcriptionLive,
              words_count: transcriptionLive.split(' ').length,
              duration_minutes: Math.round(recordingTime / 60)
            }
          })
        });

        toast.success('Réunion sauvegardée !', {
          description: `${transcriptionLive.split(' ').length} mots transcrits`
        });
        
        loadReunions();
        setTitre('');
        setTranscriptionLive('');
        setRecordingTime(0);
      }
    } catch (error) {
      console.error('Erreur sauvegarde réunion:', error);
    }
  };

  // Import d'un fichier existant
  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vérifier le type
    if (!file.type.startsWith('audio/') && !file.type.startsWith('video/')) {
      toast.error('Format non supporté', {
        description: 'Utilisez un fichier audio ou vidéo (mp3, mp4, webm, etc.)'
      });
      return;
    }

    // Vérifier la taille (500MB max)
    if (file.size > 500 * 1024 * 1024) {
      toast.error('Fichier trop volumineux', {
        description: 'Maximum 500 MB'
      });
      return;
    }

    setUploading(true);
    setUploadProgress(10);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('titre', titre || file.name.replace(/\.[^/.]+$/, ''));
      formData.append('type_reunion', 'visio');

      setUploadProgress(30);

      const res = await fetch(`${API_URL}/api/reunions/upload-direct`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      setUploadProgress(80);

      const data = await res.json();

      if (data.success) {
        toast.success('Fichier importé !', {
          description: `Transcription en cours... (${data.file_size_mb?.toFixed(1)} MB)`
        });
        loadReunions();
        setTitre('');
      } else {
        throw new Error(data.detail || 'Erreur import');
      }
    } catch (error) {
      console.error('Erreur import:', error);
      toast.error('Erreur d\'import', {
        description: error.message
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      e.target.value = '';
    }
  };

  // Voir la transcription complète
  const viewTranscription = async (reunion) => {
    try {
      const res = await fetch(`${API_URL}/api/reunions/${reunion.id}/transcription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSelectedReunion({ ...reunion, fullTranscription: data });
    } catch (error) {
      toast.error('Erreur', { description: 'Impossible de charger la transcription' });
    }
  };

  // Analyser avec l'IA
  const analyzeReunion = async (reunionId) => {
    try {
      toast.info('Analyse en cours...', { description: 'L\'IA analyse la réunion' });
      
      const res = await fetch(`${API_URL}/api/reunions/${reunionId}/analyser`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Analyse terminée !');
        loadReunions();
      } else {
        throw new Error(data.detail);
      }
    } catch (error) {
      toast.error('Erreur analyse', { description: error.message });
    }
  };

  // Rendu du statut de transcription
  const renderTranscriptionStatus = (reunion) => {
    const status = reunion.transcription?.status || reunion.enregistrement?.status;
    
    if (status === 'completed') {
      return <Badge className="bg-green-500"><CheckCircle2 className="w-3 h-3 mr-1" /> Transcrit</Badge>;
    } else if (status === 'processing' || status === 'uploaded') {
      return <Badge variant="secondary"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> En cours</Badge>;
    } else if (status === 'failed') {
      return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" /> Échec</Badge>;
    }
    return <Badge variant="outline">En attente</Badge>;
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Réunions & Transcriptions</h1>
          <p className="text-muted-foreground">Enregistrez vos appels et visios, obtenez une transcription automatique</p>
        </div>
      </div>

      {/* Zone d'enregistrement principale */}
      <Card className="border-2 border-dashed border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
        <CardContent className="p-6">
          <div className="flex flex-col items-center text-center space-y-6">
            
            {/* Titre de la réunion */}
            <div className="w-full max-w-md">
              <Label htmlFor="titre" className="text-sm text-muted-foreground">Nom de la réunion (optionnel)</Label>
              <Input
                id="titre"
                placeholder="Ex: Appel client Dupont, Visio équipe..."
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                className="mt-1"
                disabled={isRecording}
                data-testid="reunion-titre-input"
              />
            </div>

            {/* Bouton principal d'enregistrement */}
            <div className="relative">
              {/* Cercle de niveau audio */}
              {isRecording && (
                <div 
                  className="absolute inset-0 rounded-full bg-red-500/20 animate-pulse"
                  style={{ 
                    transform: `scale(${1 + audioLevel * 0.5})`,
                    transition: 'transform 0.1s ease-out'
                  }}
                />
              )}
              
              <Button
                size="lg"
                variant={isRecording ? "destructive" : "default"}
                className={`w-32 h-32 rounded-full text-lg font-semibold shadow-lg transition-all ${
                  isRecording ? 'animate-pulse' : 'hover:scale-105'
                }`}
                onClick={isRecording ? stopRecording : startRecording}
                disabled={uploading}
                data-testid="btn-record"
              >
                <div className="flex flex-col items-center gap-2">
                  {isRecording ? (
                    <>
                      <Square className="w-8 h-8" />
                      <span className="text-sm">Arrêter</span>
                    </>
                  ) : (
                    <>
                      <Mic className="w-8 h-8" />
                      <span className="text-sm">Démarrer</span>
                    </>
                  )}
                </div>
              </Button>
            </div>

            {/* Timer et statut */}
            {isRecording && (
              <div className="flex flex-col items-center gap-3">
                <div className="flex items-center gap-4 text-lg">
                  <div className="flex items-center gap-2 text-red-500">
                    <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                    <span className="font-mono font-bold">{formatTime(recordingTime)}</span>
                  </div>
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Radio className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Transcription en direct</span>
                  </div>
                  {chunksTranscribed > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {chunksTranscribed} segments transcrits
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {/* Transcription en temps réel */}
            {(isRecording || transcriptionLive) && transcriptionLive && (
              <div className="w-full max-w-2xl">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">Transcription en direct</span>
                  {isRecording && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                </div>
                <ScrollArea className="h-32 w-full rounded-md border bg-muted/30 p-3">
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {transcriptionLive || <span className="text-muted-foreground italic">En attente de parole...</span>}
                  </p>
                </ScrollArea>
                <p className="text-xs text-muted-foreground mt-1">
                  {transcriptionLive.split(' ').filter(w => w).length} mots
                </p>
              </div>
            )}

            {/* Upload en cours */}
            {uploading && (
              <div className="flex items-center gap-3 text-primary">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Envoi et transcription en cours...</span>
              </div>
            )}

            {/* Séparateur et bouton import */}
            <div className="flex items-center gap-4 w-full max-w-md">
              <div className="flex-1 h-px bg-border" />
              <span className="text-sm text-muted-foreground">ou</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Bouton importer */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="gap-2"
                disabled={isRecording || uploading}
                onClick={() => document.getElementById('file-import').click()}
                data-testid="btn-import"
              >
                <Upload className="w-4 h-4" />
                Importer un fichier
              </Button>
              <input
                type="file"
                id="file-import"
                className="hidden"
                accept="audio/*,video/*"
                onChange={handleFileImport}
              />
            </div>

            <p className="text-xs text-muted-foreground">
              Formats supportés : MP3, MP4, WebM, WAV, etc. (max 500 MB)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Liste des réunions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Mes réunions
          </CardTitle>
          <CardDescription>
            {reunions.length} réunion{reunions.length > 1 ? 's' : ''} enregistrée{reunions.length > 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : reunions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Mic className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Aucune réunion enregistrée</p>
              <p className="text-sm">Démarrez votre première réunion ci-dessus</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reunions.map((reunion) => (
                <div
                  key={reunion.id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                  data-testid={`reunion-item-${reunion.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-full ${
                      reunion.type_reunion === 'visio' ? 'bg-blue-100 text-blue-600' :
                      reunion.type_reunion === 'appel' ? 'bg-green-100 text-green-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {reunion.type_reunion === 'visio' ? <Video className="w-5 h-5" /> : <Phone className="w-5 h-5" />}
                    </div>
                    
                    <div>
                      <h3 className="font-medium">{reunion.titre}</h3>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(reunion.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        {reunion.enregistrement?.file_size_mb && (
                          <span>{reunion.enregistrement.file_size_mb.toFixed(1)} MB</span>
                        )}
                        {reunion.transcription?.duration_minutes && (
                          <span>{Math.round(reunion.transcription.duration_minutes)} min</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {renderTranscriptionStatus(reunion)}
                    
                    {reunion.transcription?.status === 'completed' && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => viewTranscription(reunion)}
                          data-testid={`btn-view-${reunion.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        
                        {!reunion.analyse && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => analyzeReunion(reunion.id)}
                            title="Analyser avec l'IA"
                            data-testid={`btn-analyze-${reunion.id}`}
                          >
                            <Brain className="w-4 h-4" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal transcription */}
      {selectedReunion && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <Card className="w-full max-w-3xl max-h-[80vh] overflow-hidden">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{selectedReunion.titre}</CardTitle>
                  <CardDescription>
                    {selectedReunion.fullTranscription?.words_count} mots • 
                    {selectedReunion.fullTranscription?.duration_minutes?.toFixed(0)} minutes
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedReunion(null)}>
                  Fermer
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Tabs defaultValue="transcription">
                <TabsList className="w-full rounded-none border-b">
                  <TabsTrigger value="transcription">Transcription</TabsTrigger>
                  {selectedReunion.analyse && <TabsTrigger value="analyse">Analyse IA</TabsTrigger>}
                </TabsList>
                
                <TabsContent value="transcription" className="m-0">
                  <ScrollArea className="h-[50vh] p-4">
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">
                      {selectedReunion.fullTranscription?.transcription || 'Transcription non disponible'}
                    </p>
                  </ScrollArea>
                </TabsContent>
                
                {selectedReunion.analyse && (
                  <TabsContent value="analyse" className="m-0">
                    <ScrollArea className="h-[50vh] p-4">
                      {selectedReunion.analyse.json ? (
                        <div className="space-y-6">
                          {/* Résumé */}
                          <div>
                            <h3 className="font-semibold text-lg mb-2">Résumé</h3>
                            <p className="text-sm text-muted-foreground">{selectedReunion.analyse.json.resume}</p>
                          </div>

                          {/* Points clés */}
                          {selectedReunion.analyse.json.points_cles?.length > 0 && (
                            <div>
                              <h3 className="font-semibold text-lg mb-2">Points clés</h3>
                              <ul className="space-y-1">
                                {selectedReunion.analyse.json.points_cles.map((point, i) => (
                                  <li key={i} className="text-sm flex items-start gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                                    {point}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Décisions */}
                          {selectedReunion.analyse.json.decisions?.length > 0 && (
                            <div>
                              <h3 className="font-semibold text-lg mb-2">Décisions prises</h3>
                              <ul className="space-y-1">
                                {selectedReunion.analyse.json.decisions.map((decision, i) => (
                                  <li key={i} className="text-sm flex items-start gap-2">
                                    <FileText className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                                    {decision}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Actions à faire */}
                          {selectedReunion.analyse.json.actions?.length > 0 && (
                            <div>
                              <h3 className="font-semibold text-lg mb-2">Actions à faire</h3>
                              <div className="space-y-2">
                                {selectedReunion.analyse.json.actions.map((action, i) => (
                                  <div key={i} className="p-3 rounded-lg bg-muted/50 border">
                                    <p className="text-sm font-medium">{action.action}</p>
                                    <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                                      {action.responsable && <span>Responsable: {action.responsable}</span>}
                                      {action.deadline && <span>Échéance: {action.deadline}</span>}
                                      {action.priorite && (
                                        <Badge variant={
                                          action.priorite === 'haute' ? 'destructive' : 
                                          action.priorite === 'moyenne' ? 'secondary' : 'outline'
                                        } className="text-xs">
                                          {action.priorite}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Actions IA proposées */}
                          {selectedReunion.analyse.json.actions_ia_proposees?.length > 0 && (
                            <div className="border-t pt-4 mt-4">
                              <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                                <Brain className="w-5 h-5 text-purple-500" />
                                Actions proposées par l'IA
                              </h3>
                              <p className="text-xs text-muted-foreground mb-3">
                                Cliquez pour exécuter automatiquement
                              </p>
                              <div className="space-y-2">
                                {selectedReunion.analyse.json.actions_ia_proposees.map((action, i) => (
                                  <Button
                                    key={i}
                                    variant={action.executed ? "secondary" : "outline"}
                                    className="w-full justify-start h-auto py-3 text-left"
                                    disabled={action.executed}
                                    onClick={async () => {
                                      try {
                                        const res = await fetch(`${API_URL}/api/reunions/${selectedReunion.id}/executer-action?action_index=${i}`, {
                                          method: 'POST',
                                          headers: { Authorization: `Bearer ${token}` }
                                        });
                                        const data = await res.json();
                                        if (data.success) {
                                          toast.success(data.message);
                                          loadReunions();
                                        } else {
                                          throw new Error(data.detail);
                                        }
                                      } catch (e) {
                                        toast.error('Erreur', { description: e.message });
                                      }
                                    }}
                                  >
                                    <div className="flex items-center gap-3">
                                      {action.type === 'intervention' && <Tractor className="w-4 h-4 text-green-500" />}
                                      {action.type === 'email' && <Mail className="w-4 h-4 text-blue-500" />}
                                      {action.type === 'rdv' && <Calendar className="w-4 h-4 text-orange-500" />}
                                      {action.type === 'tache' && <ListTodo className="w-4 h-4 text-purple-500" />}
                                      <div>
                                        <p className="text-sm font-medium">{action.description}</p>
                                        {action.executed && (
                                          <p className="text-xs text-green-500">✓ Exécuté</p>
                                        )}
                                      </div>
                                    </div>
                                  </Button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Envoyer par email */}
                          <div className="border-t pt-4 mt-4">
                            <Button
                              className="w-full"
                              onClick={async () => {
                                try {
                                  const res = await fetch(`${API_URL}/api/reunions/${selectedReunion.id}/envoyer-resume-email`, {
                                    method: 'POST',
                                    headers: { 
                                      Authorization: `Bearer ${token}`,
                                      'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({ destinataires: [user?.email] })
                                  });
                                  const data = await res.json();
                                  if (data.success) {
                                    toast.success('Résumé envoyé par email');
                                  }
                                } catch (e) {
                                  toast.error('Erreur envoi email');
                                }
                              }}
                            >
                              <Mail className="w-4 h-4 mr-2" />
                              Envoyer le résumé par email
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="prose prose-sm max-w-none">
                          <p className="whitespace-pre-wrap">
                            {selectedReunion.analyse.content}
                          </p>
                        </div>
                      )}
                    </ScrollArea>
                  </TabsContent>
                )}
              </Tabs>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
